/**
 * Manual Order Entry — Admin-only workflow for recreating offline orders.
 *
 * Reuses:
 *   - /api/orders/parse   — normalises raw pastes via master DB parser
 *   - /api/orders/quote   — live pricing preview via master DB engine
 *   - /api/admin/sticker-orders/manual — final atomic create
 *   - /api/admin/customers/search — dedupe existing customers
 *
 * The final saved order is byte-for-byte compatible with a public
 * customer submission and appears in every existing admin surface.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Search, User, Package, DollarSign, Calendar, MessageSquare, CheckCircle2, AlertTriangle, Loader2, Save, X, Copy } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import QuoteCategorySummary from "@/components/QuoteCategorySummary";

const ORDER_SOURCES = [
  { value: "instagram",  label: "Instagram" },
  { value: "facebook",   label: "Facebook" },
  { value: "tiktok",     label: "TikTok" },
  { value: "whatsapp",   label: "WhatsApp" },
  { value: "phone",      label: "Phone" },
  { value: "in_person",  label: "In-Person" },
  { value: "event",      label: "Event" },
  { value: "other",      label: "Other" },
];

// Phase 1 · Manual Payment Method
// Cash / Card / Zelle / Cash App → Paid
// Pay In Store → Awaiting Payment (order recorded but not paid yet).
const PAYMENT_METHODS = [
  { value: "cash",         label: "Cash",         status: "paid",    hint: "Order is Paid." },
  { value: "card",         label: "Card",         status: "paid",    hint: "Order is Paid." },
  { value: "zelle",        label: "Zelle",        status: "paid",    hint: "Order is Paid." },
  { value: "cashapp",      label: "Cash App",     status: "paid",    hint: "Order is Paid." },
  { value: "pay_in_store", label: "Pay In Store", status: "pending", hint: "Order is Awaiting Payment (not paid yet)." },
];

// Phase 1 · Delivery Method — reuses the existing public
// checkout delivery codes. Pickup keeps the historical local_pickup
// code (zero shipping cost); Shipping resolves to UPS Ground so the
// existing pricing engine adds the same shipping cost the public
// checkout would.
const DELIVERY_METHODS = {
  pickup:   { code: "local_pickup", label: "Pickup" },
  shipping: { code: "ups_ground",   label: "Shipping" },
};

// Phase 1 · Shipping Carriers — the label is used verbatim as the
// stored value. UPS is the default when Shipping is selected.
const SHIPPING_CARRIERS = ["UPS", "USPS", "FedEx", "Uber Delivery", "Local Delivery", "Other"];

const FULFILLMENT_OVERRIDES = [
  { value: "",                    label: "Auto (from payment status)" },
  { value: "awaiting_payment",    label: "Awaiting payment" },
  { value: "payment_verified",    label: "Payment verified" },
  { value: "preparing",           label: "Preparing" },
  { value: "ready",               label: "Ready" },
  { value: "shipped",             label: "Shipped" },
  { value: "completed",           label: "Completed" },
  { value: "cancelled",           label: "Cancelled" },
];

const BLANK_CUSTOMER = {
  first_name: "", last_name: "", email: "", phone: "", instagram_username: "",
  // Persistent TikTok handle saved on the customer record. Kept in the
  // customer document (backend-side) for continuity with existing
  // records — Phase 1 hides the input from the manual entry UI because
  // TikTok is now expressed via the Order Source field instead.
  tiktok_username: "",
  // Phase 1 · shipping_address (same shape as the public checkout).
  // Blank fields are only serialised when Delivery Method === Shipping.
  shipping_address: {
    line1: "", line2: "", city: "", state: "", postal_code: "",
    country: "United States",
  },
};
const BLANK_META = {
  source: "instagram",
  original_order_date: "",
  // Phase 1 · Payment Method drives the derived payment_status.
  // Default matches the previous default of "pending" (Pay In Store).
  payment_method: "pay_in_store",
  payment_status: "pending",
  fulfillment_status: "",
  internal_notes: "",
  amount_received: "",
  // Phase 1 · Shipping details — only serialised when the surrounding
  // delivery method is Shipping. Persisted on order.shipment on save.
  shipping_carrier: "UPS",
  tracking_number: "",
  shipped_date: "",
};
// Phase 1 · Delivery Method — Pickup default per spec.
const DEFAULT_DELIVERY = "pickup";

export default function ManualOrderEntry() {
  const nav = useNavigate();
  const [customer, setCustomer] = useState(BLANK_CUSTOMER);
  const [meta, setMeta] = useState(BLANK_META);
  // Feb-2026 · Missing-Sticker variant integrity. Manual admin orders
  // MUST carry an explicit variant — no silent WHITE default. Backend
  // enforces this with a Pydantic 422 if variant is missing or invalid.
  // Empty string here disables the Save button.
  const [variant, setVariant] = useState("");
  // Phase 1 · Delivery Method — "pickup" | "shipping". Drives which
  // delivery code we send to /orders/quote (so the pricing preview
  // reflects the same shipping cost the public checkout would apply).
  const [deliveryMethod, setDeliveryMethod] = useState(DEFAULT_DELIVERY);
  const [rawText, setRawText] = useState("");
  const [preview, setPreview] = useState(null);   // { items, unknowns, snapshot }
  const [previewErr, setPreviewErr] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  // Debounced live preview whenever the paste OR delivery method changes.
  const rawRef = useRef(rawText);
  rawRef.current = rawText;
  useEffect(() => {
    if (!rawText || !rawText.trim()) {
      setPreview(null); setPreviewErr(""); return;
    }
    const t = setTimeout(() => runPreview(rawText), 350);
    return () => clearTimeout(t);
  }, [rawText, deliveryMethod]);

  async function runPreview(text) {
    setLoadingPreview(true);
    setPreviewErr("");
    try {
      const parsed = await api.post("/orders/parse", {
        album_slug: "panini-fifa-world-cup-2026",
        raw_text: text,
      });
      const items = (parsed.data?.items || []).map((i) => ({ code: i.code, qty: i.qty }));
      const parseUnknowns = parsed.data?.unknowns || [];
      let snapshot = null;
      if (items.length) {
        const quoted = await api.post("/orders/quote", {
          album_slug: "panini-fifa-world-cup-2026",
          items,
          delivery: { code: DELIVERY_METHODS[deliveryMethod].code },
          jurisdiction: "US-FL",
        });
        snapshot = quoted.data;
      }
      // rawText may have changed while we awaited — only apply if the
      // request is still the freshest.
      if (rawRef.current !== text) return;
      setPreview({ items, parseUnknowns, snapshot });
    } catch (err) {
      if (rawRef.current !== text) return;
      setPreviewErr(formatApiError(err.response?.data?.detail) || err.message || "Preview failed.");
      setPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  }

  // Phase 1 · Shipping requires a complete address before Save enables.
  const shippingAddressComplete = (() => {
    if (deliveryMethod !== "shipping") return true;
    const a = customer.shipping_address || {};
    return ["line1", "city", "state", "postal_code", "country"]
      .every((k) => (a[k] || "").trim().length > 0);
  })();

  const canSave = (
    customer.first_name.trim() && customer.email.trim() && customer.phone.trim()
    && meta.source && meta.payment_method && shippingAddressComplete
    && preview && preview.snapshot && preview.snapshot.lines?.length > 0
    && !saving
    // Feb-2026 · Missing-Sticker variant integrity — admin must explicitly
    // pick a variant before Save can succeed. No silent WHITE default.
    && ["white","blue","gold_crumple","red","orange","purple"].includes(variant)
  );

  async function save() {
    if (!canSave) return;
    setSaving(true);
    try {
      // Phase 1 · Derive payment_status from the selected payment method.
      const methodCfg = PAYMENT_METHODS.find((m) => m.value === meta.payment_method);
      const derivedStatus = methodCfg?.status || "pending";
      const deliveryCode = DELIVERY_METHODS[deliveryMethod].code;

      const shippingAddress = deliveryMethod === "shipping"
        ? {
            line1: customer.shipping_address.line1.trim(),
            line2: (customer.shipping_address.line2 || "").trim() || null,
            city: customer.shipping_address.city.trim(),
            state: customer.shipping_address.state.trim(),
            postal_code: customer.shipping_address.postal_code.trim(),
            country: (customer.shipping_address.country || "United States").trim(),
          }
        : null;

      const payload = {
        album_slug: "panini-fifa-world-cup-2026",
        customer: {
          first_name: customer.first_name.trim(),
          last_name: (customer.last_name || "").trim(),
          email: customer.email.trim(),
          phone: customer.phone.trim(),
          instagram_username: (customer.instagram_username || "").trim() || null,
          // Persistent TikTok — retained on the customer record but no
          // longer editable from the manual entry UI (Phase 1 moved
          // TikTok into the Order Source dropdown).
          tiktok_username: (customer.tiktok_username || "").trim() || null,
          // Phase 1 · Shipping address (only when Delivery = Shipping).
          shipping_address: shippingAddress,
        },
        items: preview.items,   // already validated by the preview quote
        delivery: { code: deliveryCode },
        jurisdiction: "US-FL",
        // Feb-2026 · Explicit variant (never default silently to WHITE).
        variant,
        meta: {
          source: meta.source,
          original_order_date: meta.original_order_date || null,
          payment_status: derivedStatus,
          payment_method: meta.payment_method,
          fulfillment_status: meta.fulfillment_status || null,
          internal_notes: (meta.internal_notes || "").trim() || null,
          amount_received: meta.amount_received === "" ? null : Number(meta.amount_received),
          // Phase 1 · Shipping details — only sent when the delivery
          // method is Shipping. Backend persists them on order.shipment.
          shipping_carrier: deliveryMethod === "shipping" ? (meta.shipping_carrier || "UPS") : null,
          tracking_number:  deliveryMethod === "shipping" ? ((meta.tracking_number || "").trim() || null) : null,
          shipped_date:     deliveryMethod === "shipping" ? ((meta.shipped_date || "").trim() || null) : null,
        },
      };
      const res = await api.post("/admin/sticker-orders/manual", payload);
      const publicId = res.data?.public_id;
      toast.success(`Manual order ${publicId} created.`, { duration: 4000 });
      // Reset the form so the admin can enter the next one without a page reload.
      setCustomer(BLANK_CUSTOMER);
      setMeta(BLANK_META);
      setDeliveryMethod(DEFAULT_DELIVERY);
      setRawText("");
      setPreview(null);
      // Deep-link into the fulfillment tab for immediate follow-through.
      setTimeout(() => nav(`/admin/fulfillment/${publicId}`), 300);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="manual-order-entry">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="kicker kicker-red">Admin · New Sale (Manual Entry)</div>
          <h1 className="text-3xl font-bold mt-1">New Sale · Manual Entry</h1>
          <p className="text-[var(--smoke)] text-sm mt-1 max-w-2xl">
            Recreate an order that arrived through Instagram, WhatsApp, phone, in-person, or events.
            The same parser, pricing engine, and Master Sticker Database are used — the saved
            order is indistinguishable from a website submission.
          </p>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CustomerSection
          customer={customer}
          setCustomer={setCustomer}
          deliveryMethod={deliveryMethod}
          onCustomerPicked={() => { /* Phase 1 · no order-level TikTok anymore */ }}
        />
        <OrderMetaSection
          meta={meta} setMeta={setMeta}
          deliveryMethod={deliveryMethod} setDeliveryMethod={setDeliveryMethod}
        />
      </div>

      <StickerPasteSection
        rawText={rawText} setRawText={setRawText}
        preview={preview} loadingPreview={loadingPreview}
        previewErr={previewErr}
      />

      <ReviewFooter
        canSave={canSave} saving={saving} onSave={save}
        preview={preview} customer={customer} meta={meta}
        deliveryMethod={deliveryMethod}
        variant={variant} onVariantChange={setVariant}
      />
    </div>
  );
}

// ── Customer section ─────────────────────────────────────────────────
function CustomerSection({ customer, setCustomer, deliveryMethod, onCustomerPicked }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const dbTimer = useRef(null);

  useEffect(() => {
    if (dbTimer.current) clearTimeout(dbTimer.current);
    if (!q || q.length < 2) { setResults([]); return; }
    dbTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get("/admin/customers/search", { params: { q, limit: 8 } });
        setResults(r.data?.results || []);
        setShowResults(true);
      } catch { /* silent */ }
      finally { setSearching(false); }
    }, 300);
    return () => dbTimer.current && clearTimeout(dbTimer.current);
  }, [q]);

  function pick(r) {
    setCustomer((prev) => ({
      first_name: r.first_name || "",
      last_name: r.last_name || "",
      email: r.email || "",
      phone: r.phone || "",
      instagram_username: r.instagram_username || "",
      // Preserve any existing shipping address in the form when the
      // admin picks a customer — the customer search index does not
      // return past addresses.
      tiktok_username: r.tiktok_username || "",
      shipping_address: prev.shipping_address,
    }));
    setQ("");
    setResults([]);
    setShowResults(false);
    if (typeof onCustomerPicked === "function") onCustomerPicked(r);
    toast.success("Customer loaded from existing record.");
  }

  const setAddr = (key) => (v) => setCustomer((prev) => ({
    ...prev,
    shipping_address: { ...(prev.shipping_address || {}), [key]: v },
  }));
  const addr = customer.shipping_address || {};

  return (
    <section className="border-2 border-[var(--ink)] rounded-2xl p-5 bg-white shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <User size={20} className="text-[var(--panini-red)]" />
        <h2 className="text-lg font-bold">1. Customer</h2>
      </div>

      <label className="block mb-3">
        <span className="text-xs uppercase tracking-widest text-[var(--smoke)]">Search existing customer</span>
        <div className="relative mt-1">
          <input
            type="text" value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => setShowResults(true)}
            placeholder="Search by email, phone, name, or @instagram"
            className="w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 pr-9"
            data-testid="manual-customer-search-input"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            {searching ? <Loader2 className="animate-spin" size={16}/> : <Search size={16} className="text-[var(--smoke)]" />}
          </div>
          {showResults && results.length > 0 && (
            <ul className="absolute z-20 mt-1 w-full bg-white border-2 border-[var(--ink)] rounded-lg max-h-72 overflow-y-auto shadow-lg" data-testid="manual-customer-search-results">
              {results.map((r, i) => (
                <li key={r.email || r.last_order_public_id || i}>
                  <button
                    type="button" onClick={() => pick(r)}
                    className="w-full text-left px-3 py-2 hover:bg-red-50 border-b border-gray-100 last:border-0"
                    data-testid={`manual-customer-result-${i}`}
                  >
                    <div className="font-semibold text-sm">{r.name || `${r.first_name} ${r.last_name}`.trim() || r.email}</div>
                    <div className="text-xs text-[var(--smoke)]">{r.email} · {r.phone || "—"}{r.instagram_username ? ` · @${r.instagram_username}` : ""}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">last order {r.last_order_public_id}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="First name*" value={customer.first_name} onChange={(v) => setCustomer({ ...customer, first_name: v })} testid="manual-customer-first" />
        <Field label="Last name"   value={customer.last_name}  onChange={(v) => setCustomer({ ...customer, last_name: v })}  testid="manual-customer-last" />
        <Field label="Email*"      value={customer.email}      onChange={(v) => setCustomer({ ...customer, email: v })}      testid="manual-customer-email" type="email" />
        <Field label="Phone*"      value={customer.phone}      onChange={(v) => setCustomer({ ...customer, phone: v })}      testid="manual-customer-phone" placeholder="+13055551234" />
        <Field label="Instagram username (optional)" value={customer.instagram_username} onChange={(v) => setCustomer({ ...customer, instagram_username: v })} testid="manual-customer-instagram" placeholder="@handle" />
        {/* Phase 1 · TikTok Username field removed from the manual entry
            form — TikTok is now recorded via the Order Source dropdown. */}
      </div>

      {/* Phase 1 · Shipping address — reuses the same field labels,
          validation triggers and storage shape as the public checkout. */}
      {deliveryMethod === "shipping" && (
        <div className="mt-4 pt-4 border-t-2 border-dashed border-[var(--ink)]" data-testid="manual-shipping-address">
          <div className="text-xs uppercase tracking-widest text-[var(--panini-red)] font-bold mb-1">Ship to</div>
          <div className="text-xs text-[var(--smoke)] mb-3">Same address form used by the public checkout.</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Field label="Address line 1 *" value={addr.line1 || ""} onChange={setAddr("line1")} testid="ship-line1" placeholder="123 Main Street" />
            </div>
            <div className="sm:col-span-2">
              <Field label="Apartment / suite / unit (optional)" value={addr.line2 || ""} onChange={setAddr("line2")} testid="ship-line2" placeholder="Apt 2B" />
            </div>
            <Field label="City *"       value={addr.city || ""}        onChange={setAddr("city")}        testid="ship-city"    placeholder="Miami" />
            <Field label="State / Region *" value={addr.state || ""}   onChange={setAddr("state")}       testid="ship-state"   placeholder="FL" />
            <Field label="ZIP / Postal code *" value={addr.postal_code || ""} onChange={setAddr("postal_code")} testid="ship-zip" placeholder="33169" />
            <Field label="Country *"    value={addr.country || "United States"} onChange={setAddr("country")} testid="ship-country" placeholder="United States" />
          </div>
        </div>
      )}
    </section>
  );
}

function Field({ label, value, onChange, testid, type = "text", placeholder }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-[var(--smoke)]">{label}</span>
      <input
        type={type} value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2"
        data-testid={testid}
      />
    </label>
  );
}

// ── Order metadata section ───────────────────────────────────────────
function OrderMetaSection({ meta, setMeta, deliveryMethod, setDeliveryMethod }) {
  const methodCfg = PAYMENT_METHODS.find((m) => m.value === meta.payment_method);
  return (
    <section className="border-2 border-[var(--ink)] rounded-2xl p-5 bg-white shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <Calendar size={20} className="text-[var(--panini-red)]" />
        <h2 className="text-lg font-bold">2. Order details</h2>
      </div>

      {/* Phase 1 · Delivery Method — Pickup default. */}
      <div className="mb-4" data-testid="manual-delivery-method">
        <div className="text-xs uppercase tracking-widest text-[var(--smoke)] mb-1">Delivery Method*</div>
        <div className="flex gap-2 flex-wrap">
          {Object.entries(DELIVERY_METHODS).map(([key, m]) => {
            const active = deliveryMethod === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setDeliveryMethod(key)}
                data-testid={`manual-delivery-${key}`}
                aria-pressed={active}
                className={
                  "px-4 py-2 rounded-lg border-2 text-sm font-bold transition " +
                  (active
                    ? "border-[var(--panini-red)] bg-red-50 text-[var(--panini-red)]"
                    : "border-[var(--ink)] bg-white text-[var(--ink)] hover:bg-gray-50")
                }
              >
                {m.label}
              </button>
            );
          })}
        </div>
        <div className="text-[11px] text-[var(--smoke)] mt-1">
          {deliveryMethod === "shipping"
            ? "Shipping address is required. UPS Ground pricing applies (same as public checkout)."
            : "Local pickup — no shipping address required."}
        </div>
      </div>

      {/* Phase 1 · Shipping Carrier + Tracking Number + Shipped Date.
          Only visible when Delivery Method = Shipping. */}
      {deliveryMethod === "shipping" && (
        <div className="mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="manual-shipping-details">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-[var(--smoke)]">Shipping Carrier*</span>
            <select
              value={meta.shipping_carrier}
              onChange={(e) => setMeta({ ...meta, shipping_carrier: e.target.value })}
              className="mt-1 w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 bg-white"
              data-testid="manual-shipping-carrier"
            >
              {SHIPPING_CARRIERS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>
          <Field
            label="Tracking Number (optional)"
            value={meta.tracking_number}
            onChange={(v) => setMeta({ ...meta, tracking_number: v })}
            testid="manual-shipping-tracking"
            placeholder="1Z999AA1..."
          />
          <Field
            label="Shipped Date (optional)"
            type="date"
            value={meta.shipped_date}
            onChange={(v) => setMeta({ ...meta, shipped_date: v })}
            testid="manual-shipping-shipped-date"
          />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-[var(--smoke)]">Source*</span>
          <select
            value={meta.source} onChange={(e) => setMeta({ ...meta, source: e.target.value })}
            className="mt-1 w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 bg-white"
            data-testid="manual-order-source"
          >
            {ORDER_SOURCES.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>

        <Field label="Original order date" type="date" value={meta.original_order_date}
               onChange={(v) => setMeta({ ...meta, original_order_date: v })}
               testid="manual-order-date" />

        {/* Phase 1 · Payment Method replaces the raw Payment Status select.
            Status is auto-derived from the method the admin picks. */}
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-[var(--smoke)]">Payment Method*</span>
          <select
            value={meta.payment_method}
            onChange={(e) => setMeta({ ...meta, payment_method: e.target.value })}
            className="mt-1 w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 bg-white"
            data-testid="manual-order-payment-method"
          >
            {PAYMENT_METHODS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <div className="text-[11px] text-[var(--smoke)] mt-1">
            {methodCfg?.hint}
          </div>
        </label>

        <label className="block">
          <span className="text-xs uppercase tracking-widest text-[var(--smoke)]">Fulfillment status</span>
          <select
            value={meta.fulfillment_status}
            onChange={(e) => setMeta({ ...meta, fulfillment_status: e.target.value })}
            className="mt-1 w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 bg-white"
            data-testid="manual-order-fulfillment-status"
          >
            {FULFILLMENT_OVERRIDES.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>

        {methodCfg?.status === "paid" && (
          <Field
            label="Amount received (USD, optional)"
            type="number" value={meta.amount_received}
            onChange={(v) => setMeta({ ...meta, amount_received: v })}
            testid="manual-order-amount-received"
            placeholder="Defaults to the quoted total."
          />
        )}

        {/* Phase 1 · Order-level TikTok field removed — TikTok is now
            recorded via the Order Source dropdown above. */}
      </div>

      <label className="block mt-4">
        <span className="text-xs uppercase tracking-widest text-[var(--smoke)]">
          <MessageSquare className="inline-block" size={12} /> Internal notes
        </span>
        <textarea
          value={meta.internal_notes}
          onChange={(e) => setMeta({ ...meta, internal_notes: e.target.value })}
          rows={3}
          placeholder="e.g. Zelle deposit confirmed, DM screenshot in folder, event booth #3."
          className="mt-1 w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2"
          data-testid="manual-order-internal-notes"
        />
      </label>
    </section>
  );
}

// ── Sticker paste + live preview ─────────────────────────────────────
function StickerPasteSection({ rawText, setRawText, preview, loadingPreview, previewErr }) {
  return (
    <section className="border-2 border-[var(--ink)] rounded-2xl p-5 bg-white shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <Package size={20} className="text-[var(--panini-red)]" />
        <h2 className="text-lg font-bold">3. Sticker list</h2>
        {loadingPreview && (
          <span className="text-xs text-[var(--smoke)] flex items-center gap-1">
            <Loader2 className="animate-spin" size={12}/> parsing…
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-[var(--smoke)]">Paste the customer&apos;s list exactly as received</span>
            <textarea
              value={rawText} onChange={(e) => setRawText(e.target.value)}
              rows={12}
              placeholder={"MEX 1\nMEX-2\nBRA17\nFWC00\nCC4\nLamine Yamal"}
              className="mt-1 w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 font-mono text-sm"
              data-testid="manual-order-paste"
            />
          </label>
          <div className="mt-2 text-[11px] text-[var(--smoke)]">
            Same parser as the public wizard. Player names (e.g. <em>Lamine Yamal</em>) resolve
            automatically. Every price is pulled live from the Master Sticker Database.
          </div>
        </div>

        <PreviewPane preview={preview} previewErr={previewErr} />
      </div>
    </section>
  );
}

function PreviewPane({ preview, previewErr }) {
  if (previewErr) {
    return (
      <div className="border-2 border-red-200 bg-red-50 rounded-lg p-3 text-sm text-red-900" data-testid="manual-preview-error">
        <div className="flex items-center gap-2 font-semibold mb-1"><AlertTriangle size={14}/> Preview failed</div>
        <div>{previewErr}</div>
      </div>
    );
  }
  if (!preview) {
    return (
      <div className="border-2 border-dashed border-[var(--ink)] rounded-lg p-6 text-sm text-[var(--smoke)] text-center" data-testid="manual-preview-empty">
        Paste the customer&apos;s list on the left to see the live parsed stickers + pricing preview here.
      </div>
    );
  }
  const snap = preview.snapshot;
  const totals = snap?.totals;
  const unknowns = snap?.unknowns || [];
  return (
    <div className="space-y-3" data-testid="manual-preview">
      <div className="flex flex-wrap gap-3 text-sm">
        <Stat label="Priced lines" value={snap?.lines?.length || 0} testid="manual-preview-line-count" />
        <Stat label="Sticker count" value={totals?.sticker_count || 0} testid="manual-preview-sticker-count" />
        <Stat label="Subtotal"  value={`$${(totals?.subtotal || 0).toFixed(2)}`} testid="manual-preview-subtotal" />
        <Stat label="Tax"       value={`$${(totals?.tax?.amount || 0).toFixed(2)}`} testid="manual-preview-tax" />
        <Stat label="Total"     value={`$${(totals?.total || 0).toFixed(2)}`} testid="manual-preview-total" strong />
      </div>

      {snap?.flags?.regular_minimum_applied && (
        <div className="text-[11px] text-[var(--ink)] bg-yellow-50 border-2 border-yellow-200 rounded-lg px-3 py-2">
          Under 35 prices applied — see per-line totals.
        </div>
      )}

      <div className="max-h-[420px] overflow-y-auto pr-1" data-testid="manual-preview-table">
        <QuoteCategorySummary lines={snap?.lines || []} variant="admin" />
      </div>

      {unknowns.length > 0 && (
        <div className="border-2 border-amber-300 bg-amber-50 rounded-lg p-3 text-xs" data-testid="manual-preview-unknowns">
          <div className="font-semibold text-amber-900 mb-1 flex items-center gap-1">
            <AlertTriangle size={12}/> {unknowns.length} unknown sticker{unknowns.length > 1 ? "s" : ""} — excluded from total
          </div>
          <ul className="text-amber-900 space-y-0.5">
            {unknowns.map((u, i) => (
              <li key={i} className="font-mono">
                {u.code} <span className="text-amber-700">({u.canonical_code || u.reason})</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, strong, testid }) {
  return (
    <div className={`flex-1 min-w-[100px] border-2 rounded-lg px-3 py-2 ${strong ? "border-[var(--panini-red)] bg-red-50" : "border-[var(--ink)] bg-white"}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">{label}</div>
      <div className={`text-lg ${strong ? "font-black text-[var(--panini-red)]" : "font-bold"}`}>{value}</div>
    </div>
  );
}

// ── Review footer + Save ─────────────────────────────────────────────
function ReviewFooter({ canSave, saving, onSave, preview, customer, meta, deliveryMethod, variant, onVariantChange }) {
  const total = preview?.snapshot?.totals?.total;
  const stickers = preview?.snapshot?.totals?.sticker_count;
  const methodCfg = PAYMENT_METHODS.find((p) => p.value === meta.payment_method);
  const paymentLabel = methodCfg
    ? `${methodCfg.label} · ${methodCfg.status === "paid" ? "Paid" : "Awaiting Payment"}`
    : "—";
  const sourceLabel = ORDER_SOURCES.find((s) => s.value === meta.source)?.label;
  const deliveryLabel = DELIVERY_METHODS[deliveryMethod]?.label || "—";
  return (
    <section className="border-2 border-[var(--panini-red)] rounded-2xl p-5 bg-white shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <CheckCircle2 size={20} className="text-[var(--panini-red)]" />
        <h2 className="text-lg font-bold">4. Review &amp; save</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 text-sm mb-4">
        <ReviewCard label="Customer" value={
          customer.first_name
            ? `${customer.first_name} ${customer.last_name || ""}`.trim()
            : "—"
        } sub={customer.email || "—"} testid="manual-review-customer" />
        <ReviewCard label="Source"   value={sourceLabel || "—"} sub={meta.original_order_date || "no original date"} testid="manual-review-source" />
        <ReviewCard label="Delivery" value={deliveryLabel} sub={deliveryMethod === "shipping" ? (customer.shipping_address?.city || "address required") : "Local pickup"} testid="manual-review-delivery" />
        <ReviewCard label="Payment"  value={paymentLabel} sub={meta.amount_received ? `$${Number(meta.amount_received).toFixed(2)} received` : ""} testid="manual-review-payment" />
        <ReviewCard label="Order"    value={total != null ? `$${total.toFixed(2)}` : "—"} sub={stickers != null ? `${stickers} stickers` : ""} strong testid="manual-review-total" />
      </div>
      {/* Feb-2026 · Missing-Sticker VARIANT PICKER (required). Save is
          disabled until the admin explicitly chooses a canonical variant.
          Backend enforces this with a 422 if omitted or invalid. Never
          silently defaults to WHITE. */}
      <div className="mb-4 border-2 border-[var(--ink)] bg-amber-50 rounded-xl p-3" data-testid="manual-variant-picker">
        <div className="text-[11px] uppercase tracking-widest font-bold text-[var(--ink)] mb-2">
          Sticker Variant · REQUIRED — pick one before saving
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            {v:"white",label:"White"},
            {v:"blue",label:"Blue"},
            {v:"gold_crumple",label:"Gold Crumple"},
            {v:"red",label:"Red"},
            {v:"orange",label:"Orange"},
            {v:"purple",label:"Purple"},
          ].map(({v,label}) => {
            const active = variant === v;
            return (
              <button
                key={v}
                type="button"
                onClick={() => onVariantChange(v)}
                data-testid={`manual-variant-${v}`}
                aria-pressed={active}
                className={
                  "px-3 py-1.5 rounded-lg border-2 text-xs font-bold uppercase tracking-widest transition " +
                  (active
                    ? "border-[var(--panini-red)] bg-[var(--panini-red)] text-white"
                    : "border-[var(--ink)] bg-white text-[var(--ink)] hover:bg-gray-50")
                }
              >
                {label}
              </button>
            );
          })}
        </div>
        {!variant && (
          <div className="text-[11px] text-red-700 font-bold mt-2" data-testid="manual-variant-error">
            Pick a variant. Never assumed — the customer's chosen colour must be captured explicitly.
          </div>
        )}
      </div>
      <div className="flex items-center gap-3 flex-wrap justify-end">
        <button
          type="button" disabled={saving}
          onClick={() => window.location.reload()}
          className="border-2 border-[var(--ink)] rounded-lg px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-40"
          data-testid="manual-order-reset"
        >
          <X className="inline-block mr-1" size={14}/> Reset form
        </button>
        <button
          type="button" disabled={!canSave}
          onClick={onSave}
          className="bg-[var(--panini-red)] text-white rounded-lg px-6 py-2 font-bold disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          data-testid="manual-order-save"
        >
          {saving ? <><Loader2 className="animate-spin" size={16}/> Saving…</>
                  : <><Save size={16}/> Save order</>}
        </button>
      </div>
      {!canSave && (
        <div className="mt-3 text-[11px] text-[var(--smoke)] text-right">
          Fill in customer, delivery {deliveryMethod === "shipping" ? "+ shipping address, " : ""}payment method, and paste a valid sticker list to enable saving.
        </div>
      )}
    </section>
  );
}

function ReviewCard({ label, value, sub, strong, testid }) {
  return (
    <div className={`border-2 rounded-lg px-3 py-2 ${strong ? "border-[var(--panini-red)] bg-red-50" : "border-[var(--ink)] bg-white"}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">{label}</div>
      <div className={`${strong ? "text-xl font-black text-[var(--panini-red)]" : "text-base font-bold"}`}>{value}</div>
      {sub && <div className="text-[11px] text-[var(--smoke)] truncate">{sub}</div>}
    </div>
  );
}
